import { useEffect, useState } from "react";

import { useToast } from "~/hooks/use-toast";
import { getLocationNames, useLocationState } from "~/hooks/useLocationState";
import {
  addClientAddress,
  deleteClientAddress,
  fetchClientAddresses,
  updateClientAddress,
} from "~/services/addressService";
import { Address } from "~/types/client";

export const useClientAddresses = (clientId: string | undefined) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Create two separate location state instances for add and edit forms
  const {
    selectedRegion,
    setSelectedRegion,
    selectedComuna,
    setSelectedComuna,
    availableComunas = [], // Provide default empty array
  } = useLocationState();

  const {
    selectedRegion: editSelectedRegion,
    setSelectedRegion: setEditSelectedRegion,
    selectedComuna: editSelectedComuna,
    setSelectedComuna: setEditSelectedComuna,
    availableComunas: editAvailableComunas = [], // Provide default empty array
  } = useLocationState();

  // Fetch addresses on initial load
  useEffect(() => {
    if (clientId) {
      loadAddresses();
    }
  }, [clientId]);

  const loadAddresses = async (): Promise<Address[] | undefined> => {
    if (!clientId) return;

    setIsLoading(true);
    try {
      const addressData = await fetchClientAddresses(clientId);
      setAddresses(addressData);
      setIsLoading(false);
      return addressData;
    } catch (error) {
      console.error("Error fetching addresses:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las direcciones",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const addAddress = async (values: {
    street: string;
    number: string;
    complement?: string;
    city: string;
    state?: string;
    comuna?: string;
    postalCode?: string;
    isDefault: boolean;
    regionId?: string;
    comunaId?: string;
  }) => {
    if (!clientId) return false;

    try {
      // Use form values if available, fallback to hook state
      const regionIdToUse = values.regionId || selectedRegion;
      const comunaIdToUse = values.comunaId || selectedComuna;

      const { regionName, comunaName } = getLocationNames(
        regionIdToUse,
        comunaIdToUse
      );

      const success = await addClientAddress(
        clientId,
        values,
        regionName,
        comunaName
      );

      if (success) {
        toast({
          title: "Dirección agregada",
          description: "La dirección ha sido agregada exitosamente",
        });

        setSelectedRegion("");
        setSelectedComuna("");
        loadAddresses();
        return true;
      }

      return false;
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo agregar la dirección",
        variant: "destructive",
      });
      return false;
    }
  };

  const updateAddress = async (
    addressId: string,
    values: {
      street: string;
      number: string;
      complement?: string;
      city: string;
      state?: string;
      comuna?: string;
      postalCode?: string;
      isDefault: boolean;
    }
  ) => {
    if (!clientId) return false;

    try {
      const { regionName, comunaName } = getLocationNames(
        editSelectedRegion,
        editSelectedComuna
      );

      const success = await updateClientAddress(
        clientId,
        addressId,
        values,
        regionName,
        comunaName
      );

      if (success) {
        toast({
          title: "Dirección actualizada",
          description: "La dirección ha sido actualizada exitosamente",
        });

        loadAddresses();
        return true;
      }

      return false;
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar la dirección",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      const success = await deleteClientAddress(addressId);

      if (success) {
        toast({
          title: "Dirección eliminada",
          description: "La dirección ha sido eliminada exitosamente",
        });

        loadAddresses();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la dirección",
        variant: "destructive",
      });
    }
  };

  return {
    addresses,
    isLoading,
    addAddress,
    updateAddress,
    deleteAddress: handleDeleteAddress,
    selectedRegion,
    setSelectedRegion,
    selectedComuna,
    setSelectedComuna,
    availableComunas,
    editSelectedRegion,
    setEditSelectedRegion,
    editSelectedComuna,
    setEditSelectedComuna,
    editAvailableComunas,
    fetchAddresses: loadAddresses,
  };
};
